// M3 占位 — v1 用 mock 数据，M8 接真实 API
// 用户分析:4 KPI(总粉丝/净增/昨日新增/昨日取消) + 6 维画像 + 增长来源(SourceBar) + 关注者排行(Tabs)
// 对齐 WeChat MP 「用户分析」:增长来源用横向条,关注者排行 Tab 切「近 7 / 30 天」
import { useState } from 'react'
import { Card, Tabs } from 'antd'
import PageHeader from '@/components/PageHeader'
import KPICard from '@/components/KPICard'
import TrendChart from '@/components/TrendChart'
import PieChart from '@/components/PieChart'
import SourceBar from '@/components/SourceBar'
import { useUserAnalysis } from '@/mocks/users'

type RankTab = 'read' | 'share' | 'comment'
type FollowerTab = '7d' | '30d'

export default function UserAnalysis() {
  const [rankTab, setRankTab] = useState<RankTab>('read')
  const [followerTab, setFollowerTab] = useState<FollowerTab>('7d')
  const { data, isLoading } = useUserAnalysis()
  const d = data

  return (
    <div>
      <PageHeader
        title="用户分析"
        subtitle="粉丝资产 · 用户画像 · 增长来源"
      />

      {/* 4 KPI — WeChat 口径:总粉丝 / 净增 / 昨日新增 / 昨日取消 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard label="总粉丝" value={d?.kpi.cumulate ?? null} unit="人" loading={isLoading} />
        <KPICard
          label="净增"
          value={d?.kpi.netUser ?? null}
          unit="人"
          delta={d?.kpi.netUser && d?.kpi.cumulate ? (d.kpi.netUser / d.kpi.cumulate) * 100 : undefined}
          deltaLabel="%"
          loading={isLoading}
        />
        <KPICard label="昨日新增" value={d?.kpi.newUser ?? null} unit="人" loading={isLoading} />
        <KPICard label="昨日取消" value={d?.kpi.cancelUser ?? null} unit="人" loading={isLoading} />
      </div>

      {/* 趋势 */}
      <Card title="粉丝增长趋势 (30d)" size="small" className="mb-6">
        <TrendChart data={d?.fanTrend30d} height={260} area />
      </Card>

      {/* 6 维度画像 */}
      <Card title="用户画像 (6 维度)" size="small" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { key: 'gender', title: '性别' },
            { key: 'age', title: '年龄' },
            { key: 'region', title: '地域 TOP' },
            { key: 'device', title: '终端' },
            { key: 'model', title: '机型 TOP' },
            { key: 'language', title: '语言' },
          ].map((dim) => (
            <div key={dim.key} className="border border-ink-100 rounded p-3">
              <h4 className="text-sm font-medium text-ink-700 mb-2">{dim.title}</h4>
              {dim.key === 'region' || dim.key === 'model' ? (
                <SourceBar data={d?.attrs[dim.key as 'region' | 'model'] ?? []} maxItems={8} />
              ) : (
                <PieChart data={d?.attrs[dim.key as 'gender' | 'age' | 'device' | 'language'] ?? []} height={200} />
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* 增长来源(SourceBar 横向条) + 关注者排行(Tabs 7/30 天) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="增长来源分布" size="small">
          <SourceBar
            data={(d?.source ?? []).slice().sort((a, b) => b.value - a.value)}
            maxItems={10}
          />
        </Card>
        <Card title="关注者排行榜" size="small">
          <div className="mb-3">
            <Tabs
              size="small"
              activeKey={followerTab}
              onChange={(k) => setFollowerTab(k as FollowerTab)}
              items={[
                { key: '7d', label: '近 7 天' },
                { key: '30d', label: '近 30 天' },
              ]}
            />
          </div>
          <Tabs
            activeKey={rankTab}
            onChange={(k) => setRankTab(k as RankTab)}
            items={[
              {
                key: 'read',
                label: '阅读最多',
                children: <SourceBar data={d?.rankRead ?? []} maxItems={10} />,
              },
              {
                key: 'share',
                label: '分享最多',
                children: <SourceBar data={d?.rankShare ?? []} maxItems={10} />,
              },
              {
                key: 'comment',
                label: '留言最多',
                children: <SourceBar data={d?.rankComment ?? []} maxItems={10} />,
              },
            ]}
          />
        </Card>
      </div>
    </div>
  )
}
